import {
    VacationReportItem,
    CreateVacationReportItemByVacation,
    CreateEmptyVacationReportItemByEmployee,
    Vacation,
    isRenderableVacationState
} from '../../../features/vacation/types';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {getVacations} from '../../../features/vacation/slice';
import { setVacationReportData } from '../../../features/vacation';
import { REmployees } from '../../../features/roles/types';
import { canSeeOtherVacations } from '../../../features/profile/entityPermissions';

const convertVacationsToVacationReportItem = (
    employee: REmployees,
    vacations: Vacation[],
    employeesById: Map<number, string>
): VacationReportItem[] => {
    const renderableVacations = vacations?.filter((vacation) => isRenderableVacationState(vacation.state)) ?? [];

    if (renderableVacations.length > 0) {
        return renderableVacations.map((vacation) => (
            CreateVacationReportItemByVacation(employee.id, employee.name, vacation, employeesById)
        ));
    }
    
    const temp = CreateEmptyVacationReportItemByEmployee(employee.id, employee.name)
    return  [temp];
}

const useVacationReportLoader = () =>
{
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const lastRequestKeyRef = useRef<string>('');
    const vacations = useAppSelector((selector) => selector.vacations.items);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const profile = useAppSelector((selector) => selector.profile);
    const statesFilter = useAppSelector((selector) => selector.vacationsReport.filter.propertyFilter.selectedVacationStates);
    const generalFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter);
    const { startDate, endDate } = generalFilter.dateRangeFilter;
    const selectedEmployeesFilter = generalFilter.propertyFilter.selectedEmployees;
    const canViewOtherVacations = canSeeOtherVacations(profile);
    const selectedEmployeeIds = useMemo(
        () => selectedEmployeesFilter?.map((x) => (x.originalId as number)) ?? [],
        [selectedEmployeesFilter]
    );
    const selectedStateIds = useMemo(
        () => statesFilter?.map((x) => (x.originalId as number)) ?? [],
        [statesFilter]
    );
    const requestEmployeeIds = useMemo(
        () => canViewOtherVacations
            ? selectedEmployeeIds
            : [profile.id].filter((id) => id > 0),
        [canViewOtherVacations, profile.id, selectedEmployeeIds]
    );
    const requestKey = useMemo(
        () => JSON.stringify({
            startDate,
            endDate,
            employees: requestEmployeeIds,
            states: selectedStateIds,
        }),
        [endDate, requestEmployeeIds, selectedStateIds, startDate]
    );

    useEffect(() => {
        if (startDate === '' || endDate === '') {
            return;
        }
        if (profile.id <= 0) {
            return;
        }
        if (lastRequestKeyRef.current === requestKey) {
            return;
        }

        lastRequestKeyRef.current = requestKey;
        setLoading(true);

        dispatch(getVacations({
            dateFrom: startDate,
            dateTo: endDate,
            employees: requestEmployeeIds,
            states: selectedStateIds
        })).finally(() => setLoading(false));
    }, [dispatch, endDate, profile.id, requestEmployeeIds, requestKey, selectedStateIds, startDate]);

    useEffect(() => {
        if (!vacations && !employees) {
            return;
        }
        
        const selectedEmployees = generalFilter.propertyFilter.selectedEmployees.map((x) => x.originalId);
        const selectedStates = statesFilter.map((x) => x.originalId);
        const ownEmployee = employees.find((employee) => employee.id === profile.id)
            ?? { id: profile.id, name: profile.name, isDefault: false };
        const visibleEmployees = canViewOtherVacations
            ? employees
            : [ownEmployee].filter((employee) => employee.id > 0);
        const employeesById = new Map(employees.map((employee) => [employee.id, employee.name]));

        const items: VacationReportItem[] = visibleEmployees.map(employee => {
            const employeeVacations = convertVacationsToVacationReportItem(
                employee,
                vacations.filter((vacation) => vacation.employeeId == employee.id),
                employeesById
            );
            return [...employeeVacations];
        }).flat(1);

        const filteredItems: VacationReportItem[] = items
            .filter((x) => (canViewOtherVacations && selectedEmployees.length > 0 ? selectedEmployees.includes(x.employeeId) : true)
            && (selectedStates.length > 0 ? selectedStates.includes(x.state) : true));

        setVacationReportData(filteredItems);

    }, [canViewOtherVacations, employees, generalFilter.propertyFilter.selectedEmployees, profile.id, profile.name, statesFilter, vacations]);

    return { data: useAppSelector((store) => store.vacationsReport.data), loading, dateFilter: generalFilter.dateRangeFilter};
}

export default useVacationReportLoader;
